import { TAuthConfig, TRefreshTokenExpiredEvent } from "react-oauth2-code-pkce";

export const KEYCLOAK_OPENID_SCOPE = "openid profile email";

export function keycloakOpenIdUrl(
    authority: string,
    path: "auth" | "token" | "logout",
): string {
    return `${authority.replace(/\/$/, "")}/protocol/openid-connect/${path}`;
}

export function buildKeycloakLogoutUrl(options: {
    authority: string;
    clientId: string;
    postLogoutRedirectUri: string;
    idToken?: string;
}): string {
    const url = new URL(keycloakOpenIdUrl(options.authority, "logout"));
    url.searchParams.set("client_id", options.clientId);
    url.searchParams.set(
        "post_logout_redirect_uri",
        options.postLogoutRedirectUri,
    );
    if (options.idToken) {
        url.searchParams.set("id_token_hint", options.idToken);
    }

    return url.toString();
}

export function buildAdminSingleLogoutUrl(options: {
    adminUrl: string;
    clientId: string;
    postLogoutRedirectUri: string;
    idToken?: string;
}): string {
    const url = new URL(
        `${options.adminUrl.replace(/\/$/, "")}/auth/keycloak/single-logout`,
    );
    url.searchParams.set("client_id", options.clientId);
    url.searchParams.set("return_to", options.postLogoutRedirectUri);
    if (options.idToken) {
        url.searchParams.set("id_token_hint", options.idToken);
    }

    return url.toString();
}

export function normalizeOidcRedirectUri(url?: string): string {
    if (url && url !== "") {
        try {
            return new URL(url).origin;
        } catch {
            return url.replace(/\/$/, "");
        }
    }

    if (typeof window !== "undefined") {
        return window.location.origin;
    }

    return "";
}

export function isKeycloakAuthEnabled(provider?: string): boolean {
    return provider === "keycloak";
}

export function adminKeycloakSsoUrl(adminUrl: string): string {
    return `${adminUrl.replace(/\/$/, "")}/filament-socialite/oauth/keycloak`;
}

export function adminAppEntryUrl(adminUrl: string, provider?: string): string {
    const origin = adminUrl.replace(/\/$/, "");

    return isKeycloakAuthEnabled(provider)
        ? adminKeycloakSsoUrl(origin)
        : origin;
}

function onRefreshTokenExpire(event: TRefreshTokenExpiredEvent): void {
    if (
        window.confirm(
            "Session expired. Refresh page to continue using the site?",
        )
    ) {
        event.logIn();
    }
}

export function buildPassportAuthConfig(env: {
    authUrl?: string;
    clientId?: string;
    portalUrl?: string;
}): TAuthConfig {
    const authUrl = (env.authUrl ?? "").replace(/\/$/, "");

    return {
        clientId: env.clientId ?? "",
        authorizationEndpoint: `${authUrl}/oauth/authorize`,
        tokenEndpoint: `${authUrl}/oauth/token`,
        redirectUri: env.portalUrl ?? "",
        storage: "local",
        autoLogin: true,
        onRefreshTokenExpire,
    };
}

export function buildKeycloakAuthConfig(env: {
    authority?: string;
    clientId?: string;
    portalUrl?: string;
}): TAuthConfig {
    const authority = env.authority ?? "";
    const clientId = env.clientId ?? "";
    const portalUrl = normalizeOidcRedirectUri(env.portalUrl);

    return {
        clientId,
        authorizationEndpoint: keycloakOpenIdUrl(authority, "auth"),
        tokenEndpoint: keycloakOpenIdUrl(authority, "token"),
        redirectUri: portalUrl,
        scope: KEYCLOAK_OPENID_SCOPE,
        storage: "local",
        autoLogin: true,
        onRefreshTokenExpire,
    };
}

export function buildPortalAuthConfig(env: {
    provider?: string;
    authority?: string;
    keycloakClientId?: string;
    authUrl?: string;
    passportClientId?: string;
    portalUrl?: string;
}): TAuthConfig {
    if (!isKeycloakAuthEnabled(env.provider)) {
        return buildPassportAuthConfig({
            authUrl: env.authUrl,
            clientId: env.passportClientId,
            portalUrl: env.portalUrl,
        });
    }

    return buildKeycloakAuthConfig({
        authority: env.authority,
        clientId: env.keycloakClientId,
        portalUrl: env.portalUrl,
    });
}
